import { useCallback, useEffect, useMemo, useState } from 'react';
import { SearchViewModelItem } from './SearchViewModelItem';
import { useParams } from 'react-router';
import type { RouteParams } from '@user/pages/RouteParams';
import { useAtomValue } from 'jotai';
import { searchResultsAtom, type FolderJSON, type WorkspaceJSON } from './trpcSearchResultState';
import { sidebarSearchKeywordAtom } from '../sidebarSearchKeywordAtom';
import type { RootFolderTree } from '@workspace/view-model-folder/domain/FolderTree';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCaretDown, faCaretRight } from '@fortawesome/free-solid-svg-icons';
import { FolderItemTitle } from '../../items';
import { classNames } from '@framework/utils';

type Props = {
  workspace: WorkspaceJSON;
  folder: FolderJSON;
  rootFolderTree: RootFolderTree;
};

export const SearchFolderItem: React.FC<Props> = ({ workspace, folder, rootFolderTree }: Props) => {
  const [isOpen, setIsOpen] = useState(false);

  const { folderId: folderIdParam } = useParams<RouteParams>();
  const isSelected = useMemo(() => folder.id === folderIdParam, [folder.id, folderIdParam]);

  const trpcResults = useAtomValue(searchResultsAtom);

  const searchKeyword = useAtomValue(sidebarSearchKeywordAtom);

  useEffect(() => {
    if (searchKeyword) setIsOpen(true);
  }, [searchKeyword]);

  const folders = useMemo(() => {
    return trpcResults.results.folders;
  }, [trpcResults]);

  const viewModels = useMemo(() => {
    return trpcResults.results.viewModels;
  }, [trpcResults]);

  const viewModelTags = useMemo(() => {
    return trpcResults.results.viewModelTags;
  }, [trpcResults]);

  const sortedFolders = useMemo(() => {
    return rootFolderTree
      .getFolderIdsOf(folder.id)
      .map((fid) => folders[fid])
      .filter((folder) => !!folder)
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [rootFolderTree, folders, folder.id]);

  const sortedViewModels = useMemo(() => {
    const targetViewModelIds = rootFolderTree.getViewModelIdsOf(folder.id);

    return targetViewModelIds
      .map((viewModelId) => viewModels[viewModelId])
      .filter((viewModel) => !!viewModel)
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [rootFolderTree, viewModels, folder.id]);

  const toggleFolder = useCallback(() => {
    setIsOpen((prev) => !prev);
  }, []);

  return (
    <div className="relative">
      <div className="flex items-center">
        <button className="mr-1 focus:outline-none" onClick={toggleFolder}>
          <FontAwesomeIcon icon={isOpen ? faCaretDown : faCaretRight} className="size-4" />
        </button>
        <div className={classNames('grow', isSelected ? 'bg-gray-300 hover:bg-gray-400' : 'hover:bg-gray-300')}>
          <FolderItemTitle
            workspaceId={workspace.id}
            folderId={folder.id}
            folderName={folder.name}
            searchKeyword={searchKeyword}
          />
        </div>
      </div>

      {isOpen && (
        <div className="ml-4">
          <>
            {sortedFolders.map((folder) => (
              <SearchFolderItem key={folder.id} workspace={workspace} folder={folder} rootFolderTree={rootFolderTree} />
            ))}
            {sortedViewModels.map((viewModel) => (
              <SearchViewModelItem
                key={viewModel.id}
                groupId={workspace.groupId}
                viewModel={viewModel}
                viewModelTags={viewModelTags[viewModel.id] ?? []}
                isIndividualPermissionEnabled={workspace.isIndividualPermissionEnabled}
              />
            ))}
          </>
        </div>
      )}
    </div>
  );
};
